import { createContext, useContext } from 'react';
import type { MutableRefObject } from 'react';
import type { ExitReason, LogData, LogLevel } from '../../models';
import { TermSet } from '../../models';
import type { EmitMetric, TrackEvent } from './clickstream/events';
import type { IdentityCheckQuestion } from '@zg-rentals/ts-id-verification-client';

function noop(): void {}

export interface PidAnswers {
  firstName: string;
  middleName: string;
  lastName: string;
  currentAddress: string;
  currentCity: string;
  currentState: string;
  currentZip: string;
  phone: string;
  dob: string;
  ssn: string;
}

export interface IdVerificationContextOptions {
  id: string;
  termSet: TermSet;
  skipLandingStep: boolean;
  reauthenticating: boolean;
  handleScreeningReports: boolean;
  latestOnSubmitPidForm: MutableRefObject<(answers: PidAnswers) => void>;
  latestOnExit: (exitReason: { reason: ExitReason; success: boolean }) => void | Promise<void>;
  latestTrackEvent: MutableRefObject<TrackEvent>;
  latestEmitMetric: MutableRefObject<EmitMetric>;
  latestLog: MutableRefObject<(logLevel: LogLevel, logData: LogData) => void>;
  kiqQuestionSet: Array<IdentityCheckQuestion>;
  setKiqQuestionSet: (questionSet: Array<IdentityCheckQuestion>) => void;
  pidAnswers: PidAnswers;
  setPidAnswers: (callback: (pidAnswers: PidAnswers) => PidAnswers) => void;
  pidRemainingAttempts: number | null;
  setPidRemainingAttempts: (remainingAttempts: number | null) => void;
  isOnExitLoading?: boolean;
  hasInitializedWizard: boolean;
  setHasInitializedWizard: (initialized: boolean) => void;
}

export const DEFAULT_OPTIONS: IdVerificationContextOptions = {
  id: 'id-verification',
  termSet: TermSet.RenterScreening,
  skipLandingStep: false,
  reauthenticating: false,
  handleScreeningReports: false,
  latestOnSubmitPidForm: { current: noop },
  latestOnExit: noop,
  latestTrackEvent: { current: noop },
  latestEmitMetric: { current: noop },
  latestLog: { current: noop },
  kiqQuestionSet: [],
  setKiqQuestionSet: noop,
  pidAnswers: {
    firstName: '',
    middleName: '',
    lastName: '',
    currentAddress: '',
    currentCity: '',
    currentState: '',
    currentZip: '',
    phone: '',
    dob: '',
    ssn: '',
  },
  setPidAnswers: noop,
  pidRemainingAttempts: null,
  setPidRemainingAttempts: noop,
  isOnExitLoading: false,
  hasInitializedWizard: false,
  setHasInitializedWizard: noop,
};

const IdVerificationContext = createContext<IdVerificationContextOptions>(DEFAULT_OPTIONS);

export default IdVerificationContext;

export function useId() {
  const { id } = useContext(IdVerificationContext);
  return id;
}

export function useTermSet() {
  const { termSet } = useContext(IdVerificationContext);
  return termSet;
}

export function useReauthenticating() {
  const { reauthenticating } = useContext(IdVerificationContext);
  return reauthenticating;
}

export function useSkipLandingStep() {
  const { skipLandingStep } = useContext(IdVerificationContext);
  return skipLandingStep;
}

export function useHandleScreeningReports() {
  const { handleScreeningReports } = useContext(IdVerificationContext);
  return handleScreeningReports;
}

export function useOnSubmitPidForm() {
  const { latestOnSubmitPidForm } = useContext(IdVerificationContext);
  return latestOnSubmitPidForm.current;
}

export function useIdVerificationCallbacks() {
  const { latestOnSubmitPidForm, latestOnExit, latestTrackEvent, latestEmitMetric, latestLog, isOnExitLoading } =
    useContext(IdVerificationContext);
  return {
    onSubmitPidForm: latestOnSubmitPidForm.current,
    onExit: latestOnExit,
    trackEvent: latestTrackEvent.current,
    emitMetric: latestEmitMetric.current,
    log: latestLog.current,
    isOnExitLoading,
  };
}

export function useKiqQuestionSet() {
  const { kiqQuestionSet } = useContext(IdVerificationContext);
  return kiqQuestionSet;
}

export function useSetKiqQuestionSet() {
  const { setKiqQuestionSet } = useContext(IdVerificationContext);
  return setKiqQuestionSet;
}

export function usePidAnswers() {
  const { pidAnswers } = useContext(IdVerificationContext);
  return pidAnswers;
}

export function useSetPidAnswers() {
  const { setPidAnswers } = useContext(IdVerificationContext);
  return setPidAnswers;
}

export function usePidRemainingAttempts() {
  const { pidRemainingAttempts } = useContext(IdVerificationContext);
  return pidRemainingAttempts;
}

export function useSetPidRemainingAttempts() {
  const { setPidRemainingAttempts } = useContext(IdVerificationContext);
  return setPidRemainingAttempts;
}

export function useIsOnExitLoading() {
  const { isOnExitLoading } = useContext(IdVerificationContext);
  return isOnExitLoading;
}

export function useHasInitializedWizard() {
  const { hasInitializedWizard } = useContext(IdVerificationContext);
  return hasInitializedWizard;
}

export function useSetHasInitializedWizard() {
  const { setHasInitializedWizard } = useContext(IdVerificationContext);
  return setHasInitializedWizard;
}
